import { memo, useMemo } from 'react';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { FiMoreVertical } from 'react-icons/fi';
import { LuLoader } from 'react-icons/lu';
import { FLOW_ACTIONS } from '../../constants/flowActions';
import { FLOW_STATUS } from '@/lib/api/email-flows';
import type { FlowActionsMenuProps } from './FlowRow.types';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';

import FlowsIcon from '@/assets/icons/ic-flows.svg';
import EditIcon from '@/assets/icons/ic-edit-01.svg';
import DuplicateIcon from '@/assets/icons/ic-duplicate.svg';
import PublishIcon from '@/assets/icons/ic-publish.svg';
import HiddenIcon from '@/assets/icons/ic-hidden.svg';
import TrashIcon from '@/assets/icons/ic-trash.svg';

export const FlowActionsMenu = memo<FlowActionsMenuProps>(({ flow, onAction, isDuplicating = false }) => {
    const dropdownOptions = useMemo((): DropdownMenuOption[] => {
        const options: DropdownMenuOption[] = [
            { label: 'Edit Flow', value: FLOW_ACTIONS.EDIT_FLOW, icon: FlowsIcon },
            { label: 'Edit Emails', value: FLOW_ACTIONS.EDIT_EMAILS, icon: EditIcon },
            { label: 'Duplicate Flow', value: FLOW_ACTIONS.DUPLICATE, icon: DuplicateIcon },
        ];

        if (flow.status === FLOW_STATUS.DRAFT) {
            options.push({ label: 'Publish', value: FLOW_ACTIONS.PUBLISH, icon: PublishIcon });
        } else if (flow.status === FLOW_STATUS.PUBLISHED && !flow.data?.started) {
            options.push({ label: 'Unpublish', value: FLOW_ACTIONS.UNPUBLISH, icon: HiddenIcon });
        }

        if (!flow.data?.started) {
            options.push({ label: '', value: `divider-${flow.flow_id}`, type: 'divider' });
            options.push({
                label: 'Delete',
                value: FLOW_ACTIONS.DELETE,
                icon: TrashIcon,
                type: 'destructive',
            });
        }

        return options;
    }, [flow.status, flow.data?.started, flow.flow_id]);

    return (
        <DropdownProvider dropdownId={`flow-menu-${flow.flow_id}`} dropdownName={`flow-menu-${flow.flow_id}`}>
            <StanDropdownMenu
                id={`flow-menu-${flow.flow_id}`}
                name={`flow-menu-${flow.flow_id}`}
                options={dropdownOptions}
                placement='bottom-end'
                onSelect={value => onAction(value.value)}
                currentValue={null}
                minWidth={176}
            >
                <button
                    className='text-gray/75 hover:text-dark hover:bg-neutral-hover/50 flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors'
                    disabled={isDuplicating}
                >
                    {isDuplicating ? (
                        <LuLoader className='size-5 animate-spin' />
                    ) : (
                        <FiMoreVertical className='size-5' />
                    )}
                </button>
            </StanDropdownMenu>
        </DropdownProvider>
    );
});

FlowActionsMenu.displayName = 'FlowActionsMenu';
